import { useState, useEffect, useMemo } from 'react';
import {
    Box, Typography, Grid, Button, Snackbar, Alert, Chip, Tabs,
    Tab, IconButton, Menu, MenuItem, Dialog, DialogTitle, DialogContent,
    DialogActions, Tooltip
} from '@mui/material';
import { Breadcrumb } from 'components';
import { DataGrid, GridRenderCellParams } from '@mui/x-data-grid';
import { GenericTable } from './Components/GenericTable';
import { getAllOrders, getOrderById, updateOrderApi, getInternalUserDespatchLocations, cloneTheOrder } from 'services';
import moment from 'moment';
import useFetch from '../../../customHooks/useFetch';
import { ClientNameDropdown } from '../../custom_component/clientNameDropdown';
import { SearchComponent, SearchFieldConfig, SearchButtonConfig } from './Components/SearchComponent';
import MoreHorizRoundedIcon from '@mui/icons-material/MoreHorizRounded';
import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined';
import ContentPasteSearchOutlinedIcon from '@mui/icons-material/ContentPasteSearchOutlined';
import FileCopyOutlinedIcon from '@mui/icons-material/FileCopyOutlined';
import PreviewIcon from '@mui/icons-material/Preview';
import { useCart } from '../../../../CartContext';
import { useSearchParams, useNavigate } from 'react-router';
import { getExternalClientPermissions, clientIdsWithExternalPermission } from '../../../../helpers/externalClientPermissions';

function a11yProps(index: number) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`,
    };
}

export const OrderHistory = () => {
    const [open, setOpen] = useState(false);
    let navigate = useNavigate();
    const [paginationModel, setPaginationModel] = useState({
        page: 1,
        pageSize: 10,
        total: 0,
        data: [],
        isLoading: false
    });
    const [selectedRow, setSelectedRow] = useState(null);
    const [dispatchLocations, setDispatchLocations] = useState([]);
    const [userInfo] = useFetch();
    const [searchParams, setSearchParams] = useSearchParams();
    const clientId = searchParams.get('clientId');
    const { selectedClientId, setSelectedClientId } = useCart();
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarError, setSnackbarError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [clearStatus, setClearStatus] = useState(0);
    const [searchFilter, setSearchFilter] = useState({
        orderId: '',
        clientName: '',
        orderName: '',
        orderedBy: '',
        despatchLocation: ''
    });
    const [actionMenuAnchorEl, setActionMenuAnchorEl] = useState<null | HTMLElement>(null);
    const [actionMenuRowId, setActionMenuRowId] = useState<number | null>(null);
    const [openCloneConfirm, setOpenCloneConfirm] = useState(false);
    const [cloneRowData, setCloneRowData] = useState<any>(null);

    const jwt: any = localStorage.getItem('token');
    const labUserCheck: any = jwt ? JSON.parse(atob(jwt.split('.')[1])) : null;

    const hasAdd = labUserCheck?.Permission?.includes('AddContainerOrder');
    const hasEdit = labUserCheck?.Permission?.includes('EditContainerOrder');
    const hasView = labUserCheck?.Permission?.includes('ViewContainerOrder');

    let defaultTabStatus = (!hasAdd && hasEdit) || (!hasAdd && !hasEdit && hasView) ? 2 : 1; // If only Edit permission, default to Awaiting Fulfilment

    // Tab mapping: Requested=1, Awaiting Fulfilment=2, Awaiting Dispatch=3, Completed=4
    const [tabStatus, setTabStatus] = useState(defaultTabStatus);

    // The dropdown lists every client - only the ones with BottleOrders permission can actually load orders
    const bottleOrderClientIds = clientIdsWithExternalPermission(
        getExternalClientPermissions(labUserCheck?.ClientPermissions),
        'BottleOrders'
    );
    const fullUserMap = userInfo?.userMap || [];
    const permittedUserMap = fullUserMap.filter((client: any) => bottleOrderClientIds.has(String(client.clientID)));

    // Single source of truth for the default client on this page: an already-valid selection
    // (e.g. the user's own manual pick) is kept as-is; otherwise falls back to the URL clientId, then a permitted client, then any client.
    // Computed synchronously so ClientNameDropdown never sees an intermediate/invalid value and never has to override it itself.
    const resolvedClientId = useMemo(() => {
        if (labUserCheck?.UserType === 'Internal' || !userInfo?.userMap) {
            return null;
        }

        const isVisibleClient = (id: any) => fullUserMap.some((client: any) => String(client.clientID) === String(id));

        const requestedClientId = clientId ? Number(clientId) : null;
        if (requestedClientId !== null && isVisibleClient(requestedClientId)) {
            return requestedClientId;
        }

        const savedClientId = localStorage.getItem('selectedClientId');
        if (savedClientId && isVisibleClient(savedClientId)) {
            return Number(savedClientId);
        }

        if (selectedClientId !== null && isVisibleClient(selectedClientId)) {
            return selectedClientId;
        }

        return permittedUserMap.length > 0
            ? permittedUserMap[0].clientID
            : (fullUserMap.length > 0 ? fullUserMap[0].clientID : null);
    }, [clientId, userInfo?.userMap, labUserCheck?.UserType, selectedClientId]);

    const activeClientId = labUserCheck?.UserType === 'Internal' ? selectedClientId : resolvedClientId;
    const hasBottleOrderPermission = labUserCheck?.UserType === 'Internal' ||
        bottleOrderClientIds.has(String(activeClientId));

    useEffect(() => {
        if (resolvedClientId !== null && selectedClientId !== resolvedClientId) {
            setSelectedClientId(resolvedClientId);
        }
    }, [resolvedClientId, selectedClientId, setSelectedClientId]);

    useEffect(() => {
        if (labUserCheck?.UserType === 'Internal') {
            if (!selectedClientId) {
                setSelectedClientId(-1);
            }
        }
    }, [selectedClientId]);

    useEffect(() => {
        const isExternalUser = labUserCheck?.UserType !== 'Internal';
        const isClientSelectionSynchronized = !isExternalUser || selectedClientId === resolvedClientId;

        if (selectedClientId !== null && isClientSelectionSynchronized) {
            if (isExternalUser && !hasBottleOrderPermission) {
                setPaginationModel(old => ({ ...old, isLoading: false, data: [], total: 0 }));
                return;
            }

            getAllOrder();
        }
    }, [paginationModel?.page, selectedClientId, resolvedClientId, tabStatus, clearStatus, hasBottleOrderPermission]);

    const getAllOrder = (filter = searchFilter) => {
        let statusId = labUserCheck?.UserType === 'Internal' ? -1 : selectedClientId;
        let status = labUserCheck?.UserType !== 'Internal' ? -1 : tabStatus;
        if (selectedClientId !== null) {
            setPaginationModel(old => ({
                ...old,
                isLoading: true
            }));
            getAllOrders(statusId, paginationModel?.page, status, filter?.orderId, filter?.clientName, filter?.orderedBy, filter?.despatchLocation, filter?.orderName).then((res: any) => {
                if (res?.status === 200) {
                    setPaginationModel(old => ({
                        ...old,
                        isLoading: false,
                        data: res.data.orders ?? [],
                        total: res.data.totalOrdersCount ?? 0
                    }));
                } else {
                    setPaginationModel(old => ({
                        ...old,
                        isLoading: false,
                        data: [],
                        total: 0
                    }));
                    setSnackbarError(true);
                    setErrorMessage(res?.data || 'Failed to fetch orders');
                }
            }).catch(() => {
                setPaginationModel(old => ({
                    ...old,
                    isLoading: false,
                    data: [],
                    total: 0
                }));
                setSnackbarError(true);
                setErrorMessage('Failed to fetch orders');
            });
        }
    }

    // For External User Order Details
    const getRowDetailsApi = (id: any) => {
        const statusId = labUserCheck?.UserType === 'Internal' ? -1 : selectedClientId;
        getOrderById(id, statusId).then((res: any) => {
            if (res?.status === 200) {
                setSelectedRow(res.data);
            } else {
                setSnackbarError(true);
                setErrorMessage(res?.data || 'Failed to fetch order details');
            }
        }).catch(() => {
            setSnackbarError(true);
            setErrorMessage('Failed to fetch order details');
        });
    }

    useEffect(() => {
        if (labUserCheck?.UserType) {
            getDespatchLocations();
        }
    }, []);

    const getDespatchLocations = () => {
        return getInternalUserDespatchLocations().then((res: any) => {
            if (res?.status === 200) {
                setDispatchLocations(res?.data);
            } else {
                setSnackbarError(true);
                setErrorMessage(res?.data || 'Failed to fetch dispatch locations');
                setDispatchLocations([]);
            }
        }).catch(() => {
            setSnackbarError(true);
            setErrorMessage('Failed to fetch dispatch locations');
            setDispatchLocations([]);
        });
    }

    // Internal User Submit function
    const handleSubmit = (obj: any, status: any, actionType: any, previousStatus: any) => {
        updateOrderApi(obj).then((res: any) => {
            if (res?.status === 200) {
                setOpen(false);
                setSnackbarOpen(true);
                let actionStatus = previousStatus === 1 ? 'Successfully Confirmed!' : (previousStatus === 2 ? 'Successfully Packed!' : (previousStatus === 3 ? 'Successfully Dispatched!' : 'Successfully Completed'));
                let actionTypeMessage = (actionType === 'saveDraft' && previousStatus === 1) ? 'Saved successfully!' : (actionType === 'reject' && previousStatus === 5 ? 'Rejected successfully!' : actionStatus);
                setSuccessMessage(actionTypeMessage);
                getAllOrder();
            } else {
                setSnackbarError(true);
                setErrorMessage(res?.data || 'Failed to update order');
            }
        }).catch((error) => {
            setSnackbarError(true);
            setErrorMessage('Failed to update order');
        });
    }

    const handleClientChange = (newClientId: number) => {
        if (selectedClientId === newClientId) return; // Prevent duplicate updates
        setSelectedClientId(newClientId);
        setPaginationModel({ ...paginationModel, page: 1 });
        setSearchParams({ clientId: newClientId.toString() }, { replace: true });
    };

    const handleActionMenuOpen = (event: React.MouseEvent<HTMLElement>, rowId: number) => {
        setActionMenuAnchorEl(event.currentTarget);
        setActionMenuRowId(rowId);
    };

    const handleActionMenuClose = () => {
        setActionMenuAnchorEl(null);
        setActionMenuRowId(null);
    };

    const handleOpenCloneConfirm = (row: any) => {
        setCloneRowData(row);
        setOpenCloneConfirm(true);
        handleActionMenuClose();
    };

    const handleCloseCloneConfirm = () => {
        setOpenCloneConfirm(false);
        setCloneRowData(null);
    };

    const handleConfirmClone = () => {
        if (cloneRowData) {
            handleCloneOrder(cloneRowData);
        }
        handleCloseCloneConfirm();
    };

    const handleCloneOrder = (row: any) => {
        cloneTheOrder(row.id).then((res: any) => {
            if (res?.status === 200) {
                setSnackbarOpen(true);
                setSuccessMessage(`Order cloned successfully`);
                getAllOrder();
                handleActionMenuClose();
                if (labUserCheck?.UserType === 'Internal' && hasAdd && hasEdit) {
                    setTabStatus(1);
                }
            } else {
                setSnackbarError(true);
                setErrorMessage(res?.data || 'Failed to clone order');
            }
        }).catch(() => {
            setSnackbarError(true);
            setErrorMessage('Failed to clone order');
        }).finally(() => {
            handleActionMenuClose();
        });
    };

    const handleViewOrder = (row: any) => {
        if (userInfo?.userType === (labUserCheck?.UserType !== 'Internal' ? 'External' : 'Internal') && row?.id) {
            getRowDetailsApi(row.id);
        }
        setOpen(true);
    };

    const handleRowCellClick = (params: GridRenderCellParams) => {
        if (params.field === 'action') {
            return;
        }

        handleViewOrder(params.row);
    };

    const renderActionMenu = (params: GridRenderCellParams) => (
        <>
            <IconButton
                aria-label="more"
                aria-controls={`action-menu-${params.row.id}`}
                aria-haspopup="true"
                onClick={e => handleActionMenuOpen(e, params.row.id)}
            >
                <MoreHorizRoundedIcon />
            </IconButton>
            <Menu
                id={`action-menu-${params.row.id}`}
                anchorEl={actionMenuAnchorEl}
                open={Boolean(actionMenuAnchorEl) && actionMenuRowId === params.row.id}
                onClose={handleActionMenuClose}
                keepMounted
                PaperProps={{
                    elevation: 0,
                    sx: {
                        minWidth: 150,
                        maxWidth: 200,
                        overflow: 'visible',
                        filter: 'drop-shadow(0px 2px 2px rgba(217,217,217))',
                        mt: 1.5,
                        '& .MuiAvatar-root': {
                            width: 32,
                            height: 32,
                            ml: -0.5,
                            mr: 1,
                        },
                        '&:before': {
                            content: '""',
                            display: 'block',
                            position: 'absolute',
                            top: 0,
                            right: 14,
                            width: 10,
                            height: 10,
                            bgcolor: 'background.paper',
                            transform: 'translateY(-50%) rotate(45deg)',
                            zIndex: 0,
                        },
                    },
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
                <MenuItem
                    onClick={() => {
                        handleActionMenuClose();
                        handleViewOrder(params.row);
                    }}
                >
                    <PreviewIcon color="primary" />&nbsp; View
                </MenuItem>
                <MenuItem onClick={() => handleOpenCloneConfirm(params.row)} disabled={(!hasAdd && !hasEdit && hasView) || !hasAdd}>
                    <FileCopyOutlinedIcon color="primary" />&nbsp; Clone
                </MenuItem>
            </Menu>
        </>
    );

    const tableColumnsExternal = [
        {
            field: 'id',
            headerName: 'Order ID',
            minWidth: 200
        },
        {
            field: 'orderName',
            headerName: 'Order Name',
            minWidth: 200
        },
        {
            field: 'despatchLocation',
            headerName: 'Dispatch Location',
            minWidth: 200
        },
        {
            field: 'orderByName',
            headerName: 'Ordered By',
            minWidth: 200
        },
        {
            field: 'orderedOn',
            headerName: 'Ordered On',
            minWidth: 200,
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <Typography variant="body2">
                        {moment(params.row.orderedOn).format('DD/MM/YYYY')}
                    </Typography>
                );
            }
        },
        {
            field: 'requiredDate',
            headerName: 'Required On',
            minWidth: 150,
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <Typography variant="body2">
                        {moment(params.row.requiredDate).format('DD/MM/YYYY')}
                    </Typography>
                );
            }
        },
        {
            field: 'status',
            headerName: 'Status',
            minWidth: 150,
            renderCell: (params: GridRenderCellParams) => {
                const status = params?.row?.status;
                let color: "default" | "primary" | "success" | "warning" | "error" = "default";
                if (status === 1) {
                    color = "warning";
                } else if (status === 2) {
                    color = "primary";
                } else if (status === 3) {
                    color = "primary";
                } else if (status === 4) {
                    color = "success";
                }
                return (
                    <Chip label={params?.row?.statusDescription} color={color} />
                );
            }
        },
        {
            field: 'comments',
            headerName: 'Comments',
            minWidth: 250
        },
        {
            field: 'action',
            headerName: 'Action',
            minWidth: 150,
            renderCell: renderActionMenu
        }
    ];

    const tableColumnsInternal = [
        {
            field: 'id',
            headerName: 'Order ID',
            minWidth: 200,
            renderCell: (params: GridRenderCellParams) => {
                const isAssociatedOrder = params.row?.associatedOrderId !== null;
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2">{params.row?.id}</Typography>
                        {isAssociatedOrder && (
                            <>
                                <Tooltip title={`This order is associated with Order #${params.row?.associatedOrderId}`}>
                                    <FileCopyOutlinedIcon color="primary" sx={{ marginTop: '4px', width: '20px', height: '20px' }} />
                                </Tooltip>
                            </>
                        )}
                    </Box>
                );
            }
        },
        {
            field: 'orderName',
            headerName: 'Order Name',
            minWidth: 200
        },
        {
            field: 'clientName',
            headerName: 'Client Name',
            minWidth: 200
        },
        {
            field: 'despatchLocation',
            headerName: 'Dispatch Location',
            minWidth: 200
        },
        {
            field: 'orderByName',
            headerName: 'Ordered By',
            minWidth: 200
        },
        {
            field: 'orderedOn',
            headerName: 'Ordered On',
            minWidth: 200,
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <Typography variant="body2">
                        {moment(params.row.orderedOn).format('DD/MM/YYYY')}
                    </Typography>
                );
            }
        },
        {
            field: 'requiredDate',
            headerName: 'Required On',
            minWidth: 150,
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <Typography variant="body2">
                        {moment(params.row.requiredDate).format('DD/MM/YYYY')}
                    </Typography>
                );
            }
        },
        {
            field: 'action',
            headerName: 'Action',
            minWidth: 120,
            renderCell: renderActionMenu
        },
        {
            field: 'comments',
            headerName: 'Comments',
            minWidth: 200,
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <Typography
                        variant="body2"
                        sx={{
                            maxHeight: 56,
                            maxWidth: 220,
                            overflow: 'auto',
                            whiteSpace: 'nowrap'
                        }}
                    >
                        {params?.row?.orderRecipient?.specialInstruction}
                    </Typography>
                )
            }
        },
        ...(tabStatus === 4 ? [{
            field: 'status',
            headerName: 'Status',
            minWidth: 150,
            renderCell: (params: GridRenderCellParams) => {
                const status = params?.row?.status;
                const statusDescription = params?.row?.statusDescription;

                let color: "default" | "primary" | "success" | "warning" | "error" | "info" = "error";
                let bgColor = '#ffebee';
                let textColor = '#d32f2f';

                if (statusDescription === 'Dispatched' || statusDescription === 'Picked Up') {
                    color = "success";
                    bgColor = '#e8f5e8';
                    textColor = '#2e7d32';
                }

                return (
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        width: '100%'
                    }}>
                        <Chip
                            label={statusDescription || 'Completed'}
                            color={color}
                            variant="filled"
                            sx={{
                                backgroundColor: bgColor,
                                color: textColor,
                                fontWeight: 600,
                                fontSize: '12px',
                                height: '28px',
                                borderRadius: '14px',
                                '& .MuiChip-label': {
                                    paddingX: 2
                                }
                            }}
                        />
                    </Box>
                )
            }
        }] : [])
    ];

    const dialogColumnsExternal = [
        {
            field: 'containerName',
            headerName: 'Container Name',
            minWidth: 200
        },
        {
            field: 'unitType',
            headerName: 'Unit Type',
            minWidth: 80
        },
        {
            field: 'multiplier',
            headerName: 'Multiplier',
            minWidth: 50
        },
        {
            field: 'orderedOn',
            headerName: 'Order On',
            minWidth: 200,
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <Typography variant="body2">
                        {moment(params.row.orderedOn).format('DD/MM/YYYY')}
                    </Typography>
                );
            }
        },
        {
            field: 'requestedQuantity',
            headerName: 'Requested Quantity',
            minWidth: 150
        },
        {
            field: 'fulfilledQuantity',
            headerName: 'Fulfilled Quantity',
            minWidth: 150
        },
        {
            field: 'comments',
            headerName: 'Comments',
            minWidth: 250
        }
    ];

    const dialogColumnsInternal = [
        {
            field: 'containerName',
            headerName: 'Container Name',
            minWidth: 200
        },
        {
            field: 'bulkType',
            headerName: 'Unit Type',
            minWidth: 80
        },
        {
            field: 'multiplier',
            headerName: 'Multiplier',
            minWidth: 50
        },
        {
            field: 'requestedQuantity',
            headerName: 'Requested Quantity',
            minWidth: 50
        },
        {
            field: 'fulfilledQuantity',
            headerName: 'Fulfilled Quantity',
            minWidth: 50,
            isEnable: true,
            validation: true
        },
        {
            field: 'comments',
            headerName: 'Comments',
            minWidth: 250,
            isEnable: true
        },
        {
            field: 'select',
            headerName: 'Action',
            minWidth: 30,
            isCheckbox: true,
            isSelectAll: true
        },
    ];

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabStatus(newValue + 1);
        setSearchFilter({ orderId: '', clientName: '', orderedBy: '', despatchLocation: '', orderName: '' });
        setPaginationModel({ ...paginationModel, page: 1 });
    };

    const fields: SearchFieldConfig[] = [
        { name: 'orderId', label: 'Search by Order ID' },
        { name: 'orderName', label: 'Search by Order Name' },
        ...(labUserCheck?.UserType === 'Internal' ? [{ name: 'client', label: 'Search by Client Name' }] : []),
        {
            name: 'despatchLocation',
            label: 'Search by Dispatch Location',
            options: dispatchLocations.map((location: any) => ({
                value: location.id ?? location,
                label: location.name ?? location
            }))
        },
        { name: 'orderByName', label: 'Search by requested by', sx: { marginTop: labUserCheck?.UserType === 'Internal' ? 2 : 0 } },
    ];

    const buttons: SearchButtonConfig[] = [
        {
            name: 'search',
            label: 'Search',
            type: 'submit' as const,
            icon: <ContentPasteSearchOutlinedIcon />,
            onClick: (values) => {
                const newFilter = { orderId: values.orderId || '', clientName: values.client || '', orderedBy: values.orderByName || '', despatchLocation: values.despatchLocation || '', orderName: values.orderName || '' };
                setSearchFilter(newFilter);
                setPaginationModel(prev => ({ ...prev, page: 1 }));
                getAllOrder(newFilter);
            },
        },
        {
            name: 'clear',
            label: 'Clear All',
            variant: 'outlined',
            icon: <ClearOutlinedIcon />,
            onClick: () => {
                const empty = { orderId: '', clientName: '', orderedBy: '', despatchLocation: '', orderName: '' };
                setSearchFilter(empty);
                setClearStatus(prev => prev + 1);
                setPaginationModel(prev => ({ ...prev, page: 1 }));
                getAllOrder(empty);
            },
        },
    ];

    let intenalUserCheck = (labUserCheck?.Permission?.includes('StandardQuotes') || labUserCheck?.Permission === 'StandardQuotes') ? '/home/viewQuote' : '/home/admin';

    let pathChange = (labUserCheck && (labUserCheck?.UserType === 'Internal')) ? intenalUserCheck : '/home';
    let nav: any = [{ name: 'Home', path: pathChange }, { name: 'Order History', path: '/home/orderHistory' }];

    let columns = (labUserCheck && (labUserCheck?.UserType !== 'Internal')) ? tableColumnsExternal : tableColumnsInternal;
    let dialogColumns = (labUserCheck && (labUserCheck?.UserType !== 'Internal')) ? dialogColumnsExternal : dialogColumnsInternal;

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '16px 0', cursor: 'pointer' }}>
                <Breadcrumb navList={nav} />
            </div>
            <Typography variant="h6" gutterBottom align="left" mb={(labUserCheck && (labUserCheck?.UserType === 'Internal')) ? 2 : 0}>
                Order History
            </Typography>
            {(labUserCheck && labUserCheck?.UserType) && (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mt: 2,
                        mb: 2
                    }}
                >
                    {userInfo && (
                        <Box>
                            <ClientNameDropdown
                                selectedClientId={(labUserCheck?.UserType === 'Internal' ? selectedClientId : resolvedClientId) || ''}
                                setSelectedClientId={handleClientChange}
                                setPaginationModel={setPaginationModel}
                                clientId={clientId || ''}
                                pageName="orderHistory"
                            />
                        </Box>
                    )}
                    {(labUserCheck?.UserType === 'Internal' && hasAdd) || (labUserCheck?.UserType !== 'Internal' && hasBottleOrderPermission) ? (
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => navigate(`/home/bottleOrderList?clientId=${selectedClientId}`)}
                        >
                            Place an Order
                        </Button>
                    ) : null}
                </Box>
            )}
            {(labUserCheck && labUserCheck?.UserType) && (
                <form onSubmit={(e) => e.preventDefault()}>
                    <SearchComponent fields={fields} buttons={buttons} resetKey={clearStatus} />
                </form>
            )}
            <Box sx={{ width: '100%' }}>
                {(labUserCheck && (labUserCheck?.UserType === 'Internal')) && (
                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        {(() => {
                            if (hasAdd && !hasEdit) {
                                // Only Add permission: show Requested only
                                return (
                                    <Tabs value={tabStatus - 1} onChange={handleTabChange} aria-label="Order History Tabs">
                                        <Tab label="Requested" {...a11yProps(0)} />
                                    </Tabs>
                                );
                            } else if ((!hasAdd && hasEdit) || (!hasAdd && !hasEdit && hasView)) {
                                // Only Edit permission: show Awaiting Fulfilment, Awaiting Dispatch, Completed
                                return (
                                    <Tabs value={tabStatus - 2} onChange={(e, v) => handleTabChange(e, v + 1)} aria-label="Order History Tabs">
                                        <Tab label="Awaiting Fulfilment" {...a11yProps(1)} />
                                        <Tab label="Awaiting Dispatch" {...a11yProps(2)} />
                                        <Tab label="Completed" {...a11yProps(3)} />
                                    </Tabs>
                                );
                            } else if (hasAdd && hasEdit) {
                                // Both permissions: show all tabs
                                return (
                                    <Tabs value={tabStatus - 1} onChange={handleTabChange} aria-label="Order History Tabs">
                                        <Tab label="Requested" {...a11yProps(0)} />
                                        <Tab label="Awaiting Fulfilment" {...a11yProps(1)} />
                                        <Tab label="Awaiting Dispatch" {...a11yProps(2)} />
                                        <Tab label="Completed" {...a11yProps(3)} />
                                    </Tabs>
                                );
                            } else {
                                // No relevant permissions: show nothing
                                return null;
                            }
                        })()}
                    </Box>
                )}
                {activeClientId === null || activeClientId === undefined ? null : !hasBottleOrderPermission ? (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                        You don't have permission to view orders for this client. Please reach out to the admin to provide access for this client.
                    </Alert>
                ) : (
                    <Grid container spacing={2} mt={2}>
                        <Grid item xs={12} sm={12} md={12}>
                            <DataGrid
                                autoHeight
                                sx={{
                                    '.MuiDataGrid-columnHeaders': {
                                        backgroundColor: '#003883',
                                        color: '#fff'
                                    },
                                    '.associated-order-row': {
                                        backgroundColor: '#fff8e1'
                                    },
                                    '.associated-order-row:hover': {
                                        backgroundColor: '#ffefc2'
                                    },
                                    '.MuiDataGrid-menuIcon .MuiSvgIcon-root': {
                                        color: '#fff'
                                    },
                                    '.MuiDataGrid-iconButtonContainer .MuiSvgIcon-root': {
                                        color: '#fff'
                                    },
                                    '.MuiDataGrid-row': {
                                        cursor: 'pointer'
                                    }
                                }}
                                columns={columns}
                                rows={paginationModel?.data ?? []}
                                rowCount={paginationModel?.total ?? 0}
                                loading={paginationModel?.isLoading}
                                page={paginationModel?.page - 1}
                                pageSize={paginationModel?.pageSize}
                                pagination
                                paginationMode="server"
                                onCellClick={handleRowCellClick}
                                getRowClassName={(params) => (
                                    labUserCheck?.UserType === 'Internal' && params.row?.associatedOrderId !== null
                                        ? 'associated-order-row'
                                        : ''
                                )}
                                onPageChange={(newPage: any) => {
                                    setPaginationModel(old => ({ ...old, page: newPage + 1 }))
                                }}
                                components={{
                                    NoRowsOverlay: () => (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', py: 4 }}>
                                            <Typography variant="body2" color="text.secondary">No records found</Typography>
                                        </Box>
                                    )
                                }}
                            />
                        </Grid>
                    </Grid>
                )}
            </Box>
            <GenericTable
                open={open}
                columns={dialogColumns}
                rows={selectedRow}
                setOpen={setOpen}
                userType={labUserCheck?.UserType}
                handleSubmit={handleSubmit}
                labUserCheck={labUserCheck}
                dispatchLocations={dispatchLocations}
                tabStatus={tabStatus}
                getRowDetailsApi={getRowDetailsApi}
            />
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={() => setSnackbarOpen(false)}
            >
                <Alert onClose={() => setSnackbarOpen(false)} severity="success" sx={{ width: '100%' }}>
                    <b>{successMessage}</b>
                </Alert>
            </Snackbar>
            <Snackbar
                open={snackbarError}
                autoHideDuration={3000}
                onClose={() => setSnackbarError(false)}
            >
                <Alert onClose={() => setSnackbarError(false)} severity="error" sx={{ width: '100%' }}>
                    <b>{errorMessage}</b>
                </Alert>
            </Snackbar>
            <Dialog
                open={openCloneConfirm}
                onClose={handleCloseCloneConfirm}
                aria-labelledby="alert-dialog-title"
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle id="alert-dialog-title">
                    Order #{cloneRowData?.id}
                </DialogTitle>
                <DialogContent>
                    <Typography>Do you want to clone this Order ?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseCloneConfirm} color="primary" variant='outlined'>
                        Cancel
                    </Button>
                    <Button onClick={handleConfirmClone} color="primary" variant="contained" autoFocus>
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};